import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import ClientLayout from '../../components/ClientLayout';
import PageHeader from '../../components/client/PageHeader';
import { clientPortalApi, clientAuthApi, ClientProfile, getErrorMessage } from '../../lib/http';

type PhoneStep = 'display' | 'edit' | 'verify' | 'verified';
type EmailStep = 'display' | 'edit' | 'verify';

const TIMEZONES = [
  { group: 'Europe', value: 'Europe/London', label: 'London', zone: 'GMT', offset: 'UTC+0' },
  { group: 'Europe', value: 'Europe/Madrid', label: 'Madrid', zone: 'CET', offset: 'UTC+1' },
  { group: 'Europe', value: 'Europe/Helsinki', label: 'Helsinki', zone: 'EET', offset: 'UTC+2' },
  { group: 'Europe', value: 'Europe/Istanbul', label: 'Istanbul', zone: 'TRT', offset: 'UTC+3' },
  { group: 'United States', value: 'America/New_York', label: 'Eastern Time', zone: 'EST', offset: 'UTC-5' },
  { group: 'United States', value: 'America/Chicago', label: 'Central Time', zone: 'CST', offset: 'UTC-6' },
  { group: 'United States', value: 'America/Denver', label: 'Mountain Time', zone: 'MST', offset: 'UTC-7' },
  { group: 'United States', value: 'America/Los_Angeles', label: 'Pacific Time', zone: 'PST', offset: 'UTC-8' },
  { group: 'United States', value: 'America/Anchorage', label: 'Alaska', zone: 'AKST', offset: 'UTC-9' },
  { group: 'United States', value: 'Pacific/Honolulu', label: 'Hawaii', zone: 'HST', offset: 'UTC-10' },
  { group: 'Other', value: 'UTC', label: 'UTC', zone: 'UTC', offset: 'UTC+0' },
];

const TimezoneSelect: React.FC<{ value: string; onChange: (value: string) => void }> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedTz = TIMEZONES.find((tz) => tz.value === value);

  const filtered = TIMEZONES.filter((tz) => {
    const q = search.toLowerCase();
    return tz.label.toLowerCase().includes(q) || tz.zone.toLowerCase().includes(q) || tz.value.toLowerCase().includes(q) || tz.offset.toLowerCase().includes(q);
  });

  const groups = filtered.reduce<Record<string, typeof TIMEZONES>>((acc, tz) => {
    if (!acc[tz.group]) acc[tz.group] = [];
    acc[tz.group].push(tz);
    return acc;
  }, {});

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
      <button
        type="button"
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) setTimeout(() => inputRef.current?.focus(), 0);
        }}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-left flex items-center justify-between focus:ring-2 focus:ring-purple-500 focus:border-transparent"
      >
        <span className={selectedTz ? 'text-gray-900' : 'text-gray-500'}>
          {selectedTz ? `${selectedTz.label} — ${selectedTz.zone} (${selectedTz.offset})` : 'Select timezone'}
        </span>
        <svg className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search timezone..."
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div className="max-h-60 overflow-y-auto">
            {Object.entries(groups).map(([group, tzs]) => (
              <div key={group}>
                <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase bg-gray-50">{group}</div>
                {tzs.map((tz) => (
                  <button
                    key={tz.value}
                    type="button"
                    onClick={() => {
                      onChange(tz.value);
                      setIsOpen(false);
                      setSearch('');
                    }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-purple-50 flex justify-between items-center ${
                      tz.value === value ? 'bg-purple-50 text-purple-700' : 'text-gray-700'
                    }`}
                  >
                    <span>{tz.label} — {tz.zone}</span>
                    <span className="text-xs text-gray-400">{tz.offset}</span>
                  </button>
                ))}
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">No timezones found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ClientProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<ClientProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  // Phone verification state
  const [phoneStep, setPhoneStep] = useState<PhoneStep>('display');
  const [newPhone, setNewPhone] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationToken, setVerificationToken] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isPhoneLoading, setIsPhoneLoading] = useState(false);

  // Email change state
  const [emailStep, setEmailStep] = useState<EmailStep>('display');
  const [newEmail, setNewEmail] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [emailResendCooldown, setEmailResendCooldown] = useState(0);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ClientProfile>();

  const currentTimezone = watch('timezone');

  useEffect(() => {
    fetchProfile();
  }, []);

  // Resend cooldown timer (phone)
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Resend cooldown timer (email)
  useEffect(() => {
    if (emailResendCooldown > 0) {
      const timer = setTimeout(() => setEmailResendCooldown(emailResendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [emailResendCooldown]);

  const fetchProfile = async () => {
    try {
      setIsLoading(true);
      const response = await clientPortalApi.profile.get();
      setProfile(response.data);
      reset(response.data);
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      setError('Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: ClientProfile) => {
    setError('');
    setSuccessMessage('');
    setIsSaving(true);

    try {
      const updateData: Partial<ClientProfile> & { phone_verification_token?: string } = {
        name: data.name,
        locale: data.locale,
        timezone: data.timezone,
      };

      // Include phone change if verified
      if (verificationToken && newPhone) {
        updateData.phone = newPhone;
        updateData.phone_verification_token = verificationToken;
      }

      const response = await clientPortalApi.profile.update(updateData);
      setProfile(response.data);
      reset(response.data);
      setVerificationToken(null);
      setNewPhone('');
      setPhoneStep('display');
      setSuccessMessage('Profile updated successfully!');

      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err: any) {
      console.error('Failed to update profile:', err);
      setError(getErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/u');
  };

  // Phone verification handlers
  const handleSendCode = async () => {
    setError('');
    setIsPhoneLoading(true);
    try {
      const normalizedPhone = newPhone.replace(/[\s\-\(\)]/g, '');
      await clientAuthApi.sendPhoneCode(normalizedPhone);
      setNewPhone(normalizedPhone);
      setPhoneStep('verify');
      setResendCooldown(60);
      setVerificationCode('');
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setIsPhoneLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setError('');
    setIsPhoneLoading(true);
    try {
      const response = await clientAuthApi.verifyPhoneCode(newPhone, verificationCode);
      if (response.data.verification_token) {
        setVerificationToken(response.data.verification_token);
        setPhoneStep('verified');
      }
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setIsPhoneLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    setError('');
    setIsPhoneLoading(true);
    try {
      await clientAuthApi.sendPhoneCode(newPhone);
      setResendCooldown(60);
      setVerificationCode('');
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setIsPhoneLoading(false);
    }
  };

  // Email change handlers
  const handleSendEmailCode = async () => {
    setEmailError(null);
    setIsEmailLoading(true);
    try {
      await clientPortalApi.emailChange.sendCode(newEmail);
      setEmailStep('verify');
      setEmailResendCooldown(60);
      setEmailOtp('');
    } catch (err) {
      setEmailError(getErrorMessage(err));
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleVerifyEmailCode = async () => {
    setEmailError(null);
    setIsEmailLoading(true);
    try {
      await clientPortalApi.emailChange.verify(emailOtp, newEmail);
      const profileResponse = await clientPortalApi.profile.get();
      setProfile(profileResponse.data);
      reset(profileResponse.data);
      setEmailStep('display');
      setNewEmail('');
      setEmailOtp('');
      setSuccessMessage('Check your new email for a confirmation link.');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setEmailError(getErrorMessage(err));
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleCancelEmailChange = async () => {
    setIsEmailLoading(true);
    try {
      await clientPortalApi.emailChange.cancel();
      const profileResponse = await clientPortalApi.profile.get();
      setProfile(profileResponse.data);
      reset(profileResponse.data);
    } catch (err) {
      setEmailError(getErrorMessage(err));
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleResendEmailCode = async () => {
    if (emailResendCooldown > 0) return;
    setEmailError(null);
    setIsEmailLoading(true);
    try {
      await clientPortalApi.emailChange.sendCode(newEmail);
      setEmailResendCooldown(60);
      setEmailOtp('');
    } catch (err) {
      setEmailError(getErrorMessage(err));
    } finally {
      setIsEmailLoading(false);
    }
  };

  if (isLoading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-600">Loading profile...</div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="max-w-3xl">
        <PageHeader title="Profile" backTo="/u" />

        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
            {successMessage}
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={() => setError('')}
              className="text-red-700 hover:text-red-900"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-6">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>

                {emailError && (
                  <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center justify-between">
                    <span>{emailError}</span>
                    <button type="button" onClick={() => setEmailError(null)} className="text-red-700 hover:text-red-900">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                )}

                {/* Display current email */}
                {emailStep === 'display' && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="email"
                        value={profile?.email || ''}
                        disabled
                        className="flex-1 px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-700 cursor-not-allowed"
                      />
                      <button
                        type="button"
                        onClick={() => { setEmailStep('edit'); setNewEmail(''); setEmailError(null); }}
                        className="px-4 py-2 text-sm font-medium text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50"
                      >
                        Change
                      </button>
                    </div>
                    {profile?.unconfirmed_email && (
                      <div className="flex items-center justify-between px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <span className="text-sm text-yellow-700">
                          Pending change to <span className="font-medium">{profile.unconfirmed_email}</span> — check your inbox
                        </span>
                        <button
                          type="button"
                          onClick={handleCancelEmailChange}
                          disabled={isEmailLoading}
                          className="text-sm font-medium text-yellow-700 hover:text-yellow-900 underline disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Edit: enter new email */}
                {emailStep === 'edit' && (
                  <div className="space-y-2">
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="Enter new email address"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500">We'll send a verification code to your current email to confirm your identity</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleSendEmailCode}
                        disabled={isEmailLoading || !newEmail.trim()}
                        className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50"
                      >
                        {isEmailLoading ? 'Sending...' : 'Send verification code'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setEmailStep('display'); setNewEmail(''); setEmailError(null); }}
                        className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Verify: enter OTP */}
                {emailStep === 'verify' && (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      Enter the code sent to <span className="font-medium text-purple-600">{profile?.email}</span>
                    </p>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={emailOtp}
                      onChange={(e) => setEmailOtp(e.target.value)}
                      placeholder="123456"
                      maxLength={6}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center text-xl tracking-widest"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleVerifyEmailCode}
                        disabled={isEmailLoading || !emailOtp.trim()}
                        className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50"
                      >
                        {isEmailLoading ? 'Verifying...' : 'Verify & change'}
                      </button>
                      <button
                        type="button"
                        onClick={handleResendEmailCode}
                        disabled={emailResendCooldown > 0 || isEmailLoading}
                        className="px-4 py-2 text-sm font-medium text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50 disabled:text-gray-400 disabled:border-gray-200"
                      >
                        {emailResendCooldown > 0 ? `Resend in ${emailResendCooldown}s` : 'Resend'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setEmailStep('edit'); setEmailOtp(''); setEmailError(null); }}
                        className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Back
                      </button>
                    </div>
                  </div>
                )}

                <p className="mt-1 text-xs text-gray-500">
                  Your login email. Requires verification to change.
                </p>
              </div>

              {/* Name */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Name
                </label>
                <input
                  id="name"
                  type="text"
                  {...register('name', {
                    maxLength: {
                      value: 100,
                      message: 'Name must be 100 characters or less',
                    },
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  aria-invalid={errors.name ? 'true' : 'false'}
                  aria-describedby={errors.name ? 'name-error' : undefined}
                />
                {errors.name && (
                  <p id="name-error" className="mt-1 text-sm text-red-600">
                    {errors.name.message}
                  </p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                  {profile?.phone_verified && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      Verified
                    </span>
                  )}
                  {profile?.phone && !profile?.phone_verified && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                      Unverified
                    </span>
                  )}
                </label>

                {/* Display current phone */}
                {phoneStep === 'display' && (
                  <div className="flex items-center gap-2">
                    <input
                      type="tel"
                      value={profile?.phone || ''}
                      disabled
                      placeholder="No phone number set"
                      className="flex-1 px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-700 cursor-not-allowed"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setPhoneStep('edit');
                        setNewPhone(profile?.phone || '');
                      }}
                      className="px-4 py-2 text-sm font-medium text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50"
                    >
                      Change
                    </button>
                  </div>
                )}

                {/* Edit: enter new phone */}
                {phoneStep === 'edit' && (
                  <div className="space-y-2">
                    <input
                      type="tel"
                      value={newPhone}
                      onChange={(e) => setNewPhone(e.target.value)}
                      placeholder="+1234567890"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500">We'll send a verification code to this number</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleSendCode}
                        disabled={isPhoneLoading || !newPhone.trim()}
                        className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50"
                      >
                        {isPhoneLoading ? 'Sending...' : 'Send Code'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setPhoneStep('display');
                          setNewPhone('');
                        }}
                        className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Verify: enter code */}
                {phoneStep === 'verify' && (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      Enter the code sent to <span className="font-medium text-purple-600">{newPhone}</span>
                    </p>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      placeholder="123456"
                      maxLength={8}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center text-xl tracking-widest"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleVerifyCode}
                        disabled={isPhoneLoading || !verificationCode.trim()}
                        className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50"
                      >
                        {isPhoneLoading ? 'Verifying...' : 'Verify'}
                      </button>
                      <button
                        type="button"
                        onClick={handleResendCode}
                        disabled={resendCooldown > 0 || isPhoneLoading}
                        className="px-4 py-2 text-sm font-medium text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50 disabled:text-gray-400 disabled:border-gray-200"
                      >
                        {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setPhoneStep('edit');
                          setVerificationCode('');
                        }}
                        className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Change number
                      </button>
                    </div>
                  </div>
                )}

                {/* Verified: show success */}
                {phoneStep === 'verified' && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-lg">
                      <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm text-green-700">
                        <span className="font-medium">{newPhone}</span> verified. Click Save Changes to apply.
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setPhoneStep('display');
                        setNewPhone('');
                        setVerificationToken(null);
                        setVerificationCode('');
                      }}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      Cancel phone change
                    </button>
                  </div>
                )}
              </div>

              {/* Locale */}
              <div>
                <label
                  htmlFor="locale"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Language
                </label>
                <select
                  id="locale"
                  {...register('locale', { required: 'Language is required' })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                  <option value="it">Italian</option>
                </select>
                {errors.locale && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.locale.message}
                  </p>
                )}
              </div>

              {/* Timezone */}
              <TimezoneSelect
                value={currentTimezone || ''}
                onChange={(value) => setValue('timezone', value, { shouldDirty: true })}
              />
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-2 bg-white text-gray-700 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-6 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </ClientLayout>
  );
};

export default ClientProfilePage;
